import { c as _c } from "react/compiler-runtime";
import { differenceInDays, subDays } from 'date-fns';
import { useContext } from 'react';
import useSWR from 'swr';
import { APIList, SeekPagination } from '../../../../../../../../typings/API.interface';
import { Event, EventWithData } from '../../../../../../../../typings/Event.interface';
import { EventAttempt } from '../../../../../../../../typings/EventAttempt.interface';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import { EventListFiltersProps } from '../../../../../typings/EventList.interface';
import { formatDate, getTimezoneAbbreviation } from '../../../../../utils/date';
import Alert from '../../../../common/base/Alert';
import Divider from '../../../../common/base/Divider';
import Link from '../../../../common/base/Link';
import Skeleton from '../../../../common/base/Skeleton';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import { Div } from '../../../../common/helpers/StyledUtils';
import JSONBrowser, { DynamicColumnHeader } from '../../../../common/JSONBrowser';
import Table, { TableBar } from '../../../../common/Table/Table';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { parseDynamicColumn, TableContext } from '../../../../contexts/TableContext';
import { EventListResults } from '../../../../hooks/useEventList';
import { useNextRelativeDate } from '../../../../hooks/useNextRelativeDate';
import usePlansMaxRetention from '../../../../hooks/usePlansMaxRetention';
import { useDatePreferences } from '../../../../hooks/useDatePreferences';
import { DashboardContext } from '../../DashboardContext';
import EventListEmptyView from '../EventListEmptyView';
import event_list_layouts from './EventListLayouts';
type Pagination = SeekPagination & {
  page_size: number;
  current_page: number;
};
const getNestedValue = (obj: any, path: (string | number)[]): any => {
  if (path.length === 0) return obj;
  const [first, ...rest] = path;
  if (!obj || typeof obj !== 'object') return undefined;
  return getNestedValue(obj[first], rest);
};

// Module-scope helper: the try/catch lives here (outside the component) so the React
// Compiler can memoize EventsList — it bails on any component whose body contains a
// try/catch statement.
const renderDynamicColumnField = (column_string: string, event: EventWithData) => {
  try {
    const parsed = parseDynamicColumn(column_string);
    let data: any;
    let value: any;
    if (parsed.root === 'path') {
      value = event.data?.path || '/';
    } else if (parsed.root === 'body') {
      const body = event.data?.body;
      data = typeof body === 'string' && parsed.path.length > 0 ? JSON.parse(body) : body || {};
    } else if (parsed.root === 'query') {
      data = event.data?.parsed_query || {};
    } else {
      data = event.data?.headers || {};
    }
    if (parsed.root !== 'path') {
      value = getNestedValue(data, parsed.path);
    }
    const value_string = typeof value === 'object' ? JSON.stringify(value) : String(value);
    if (value === undefined || value === null) {
      return <Text muted size="xs">
          -
        </Text>;
    }
    return <Tooltip side="bottom" fill disabled={value_string.length <= 25} delay={500} element={<Div style={{
      maxHeight: '300px',
      width: '300px',
      overflow: 'auto'
    }} p={{
      x: 1
    }}>
            <Text size="xs" semi muted m={{
        b: 1
      }}>
              Payload
            </Text>
            <JSONBrowser json={value} compact />
          </Div>}>
        <Text mono size="xs" ellipsis>
          {value_string}
        </Text>
      </Tooltip>;
  } catch (e) {
    return <Text muted size="xs">
        -
      </Text>;
  }
};
interface EventsListProps extends EventListResults {
  layout: 'http' | 'cli';
  active_filters?: object;
  selected_event_id?: string;
  filters: EventListFiltersProps;
  selected_attempt_id: string;
  total_count?: number;
  pagination: Pagination | undefined;
  onEventSelected(id: string | null): void;
  onAttemptSelected(id: string): void;
  onPaginationChanged(filters: object): void;
  event_actions: { [k in 'retry' | 'bookmark' | 'cancel' | 'replay']: (event: Event) => void };
  event_attempts?: APIList<EventAttempt>;
  has_new_results?: boolean;
  onRefresh?: () => void;
}
export const event_status_explainer = {
  QUEUED: 'Event is queued and is waiting to be delivered to your destination.',
  HOLD: 'Event is on hold and waiting for the connection to be unpaused.',
  SCHEDULED: 'Event is scheduled to be delivered at a later time.',
  FAILED: "Event couldn't to be delivered to your destination.",
  CANCELLED: 'This event was cancelled and never delivered. If you want to add it back to the delivery queue, select the retry button next to the event status.'
};
const EventsList: React.FC<EventsListProps> = t0 => {
  const $ = _c(107);
  const {
    selected_event_id,
    onEventSelected,
    selected_attempt_id,
    onAttemptSelected,
    layout,
    onPaginationChanged,
    filters,
    events,
    outdated_ids,
    count,
    fetched,
    prev,
    next,
    total_count,
    event_actions,
    event_attempts,
    pagination,
    is_progressive_loading,
    scanned_until,
    has_new_results,
    onRefresh
  } = t0;
  const {
    timezone
  } = useDatePreferences();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    active_cli_sessions,
    team,
    subscription
  } = useContext(DashboardContext);
  const {
    max_retention_days
  } = usePlansMaxRetention();
  const {
    next_relative_date,
    updateFilters
  } = useNextRelativeDate(filters, subscription);
  const {
    dynamic_columns,
    reorderDynamicColumns,
    updateDynamicColumnWidth
  } = useContext(TableContext);
  const unique_webhook_ids = events ? Array.from(new Set(events.map(_temp))).sort() : [];
  const unique_client_ids = events ? Array.from(new Set(events.map(_temp2).filter(Boolean))).sort() : [];
  let t1;
  if ($[0] !== HookdeckAPI.webhooks || $[1] !== unique_webhook_ids) {
    t1 = () => HookdeckAPI.webhooks.list(unique_webhook_ids.length > 0 ? {
      id: unique_webhook_ids
    } : {
      limit: 1
    });
    $[0] = HookdeckAPI.webhooks;
    $[1] = unique_webhook_ids;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: webhooks
  } = useSWR(APIMethodKeys.webhooks.list(unique_webhook_ids.length > 0 ? {
    id: unique_webhook_ids
  } : {
    limit: 1
  }), t1);
  let t2;
  if ($[3] !== HookdeckAPI.cli_clients || $[4] !== unique_client_ids) {
    t2 = () => HookdeckAPI.cli_clients.list({
      id: unique_client_ids
    });
    $[3] = HookdeckAPI.cli_clients;
    $[4] = unique_client_ids;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data: cli_clients
  } = useSWR(unique_client_ids.length > 0 && APIMethodKeys.cli_clients.list({
    id: unique_client_ids
  }), t2);
  let alert_top;
  let draggable_column_indices;
  let has_request_filter;
  let headers;
  let loading;
  let onRowSelected;
  let t3;
  let t4;
  let widths;
  if ($[6] !== active_cli_sessions || $[7] !== cli_clients || $[8] !== count || $[9] !== dynamic_columns || $[10] !== event_actions || $[11] !== event_attempts || $[12] !== events || $[13] !== fetched || $[14] !== filters.cli_user_id || $[15] !== filters.request || $[16] !== layout || $[17] !== onAttemptSelected || $[18] !== onEventSelected || $[19] !== outdated_ids || $[20] !== selected_attempt_id || $[21] !== selected_event_id || $[22] !== unique_client_ids || $[23] !== unique_webhook_ids || $[24] !== webhooks) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const webhooks_by_id = webhooks && webhooks.models.reduce(_temp3, {});
      const cli_clients_by_id = cli_clients && cli_clients.models.reduce(_temp4, {});
      loading = events === undefined || !fetched || unique_webhook_ids.length > 0 && webhooks === undefined || unique_client_ids.length > 0 && cli_clients === undefined;
      has_request_filter = filters.request && Object.values(filters.request).some(_temp5);
      const {
        headers: base_headers,
        widths: base_widths,
        getEventRowFields,
        getAttemptRowFields
      } = event_list_layouts[layout]();
      const dynamic_col_width = {
        min: 200,
        max: 200
      };
      const all_widths = [...base_widths, ...dynamic_columns.map(column_string => {
        const parsed = parseDynamicColumn(column_string);
        if (parsed.width) {
          return {
            min: parsed.width,
            max: parsed.width
          };
        }
        return dynamic_col_width;
      })];
      const all_headers = [...base_headers, ...dynamic_columns.map(_temp6)];
      let t5;
      bb1: {
        const base_column_count = base_headers.length;
        const dynamic_column_count = dynamic_columns.length;
        if (dynamic_column_count === 0) {
          let t6;
          if ($[34] !== base_column_count) {
            t6 = Array.from({
              length: base_column_count
            }, _temp7);
            $[34] = base_column_count;
            $[35] = t6;
          } else {
            t6 = $[35];
          }
          t5 = t6;
          break bb1;
        }
        let order;
        if ($[36] !== base_column_count || $[37] !== dynamic_column_count) {
          order = [];
          order.push(0, 1);
          for (let i = 0; i < dynamic_column_count; i++) {
            order.push(base_column_count + i);
          }
          for (let i_0 = 2; i_0 < base_column_count; i_0++) {
            order.push(i_0);
          }
          $[36] = base_column_count;
          $[37] = dynamic_column_count;
          $[38] = order;
        } else {
          order = $[38];
        }
        t5 = order;
      }
      const column_order = t5;
      widths = column_order.map(index_0 => all_widths[index_0]).filter(_temp8);
      headers = column_order.map(index_1 => all_headers[index_1]).filter(_temp9);
      let t6;
      bb2: {
        if (dynamic_columns.length === 0) {
          let t7;
          if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
            t7 = [];
            $[39] = t7;
          } else {
            t7 = $[39];
          }
          t6 = t7;
          break bb2;
        }
        let t7;
        if ($[40] !== dynamic_columns.length) {
          t7 = Array.from({
            length: dynamic_columns.length
          }, _temp10);
          $[40] = dynamic_columns.length;
          $[41] = t7;
        } else {
          t7 = $[41];
        }
        t6 = t7;
      }
      draggable_column_indices = t6;
      const is_filtering_cli = filters.cli_user_id && filters.cli_user_id.length > 0;
      if (active_cli_sessions && active_cli_sessions.length === 0 && is_filtering_cli) {
        alert_top = "Connect your CLI to receive new events";
      }
      if (!loading && events && count === 0) {
        let t7;
        if ($[42] !== alert_top) {
          t7 = alert_top && <><Alert warning={true} no_border={true}>{alert_top}</Alert><Divider /></>;
          $[42] = alert_top;
          $[43] = t7;
        } else {
          t7 = $[43];
        }
        let t8;
        if ($[44] !== webhooks) {
          t8 = <EventListEmptyView webhooks={webhooks} />;
          $[44] = webhooks;
          $[45] = t8;
        } else {
          t8 = $[45];
        }
        let t9;
        if ($[46] !== t7 || $[47] !== t8) {
          t9 = <>{t7}{t8}</>;
          $[46] = t7;
          $[47] = t8;
          $[48] = t9;
        } else {
          t9 = $[48];
        }
        t4 = t9;
        break bb0;
      }
      let t7;
      if ($[49] !== onAttemptSelected || $[50] !== onEventSelected || $[51] !== selected_event_id) {
        t7 = id => {
          if (selected_event_id === id) {
            onEventSelected(null);
          }
          return id ? id.indexOf("atm_") === 0 ? onAttemptSelected(id) : onEventSelected(id) : onEventSelected(null);
        };
        $[49] = onAttemptSelected;
        $[50] = onEventSelected;
        $[51] = selected_event_id;
        $[52] = t7;
      } else {
        t7 = $[52];
      }
      onRowSelected = t7;
      t3 = loading ? [] : events.reduce((rows, event_1) => {
        const outdated = outdated_ids.includes(event_1.id);
        const dynamic_fields = dynamic_columns.map(column_string_1 => renderDynamicColumnField(column_string_1, event_1));
        const base_fields = getEventRowFields(event_1, outdated, event_actions, webhooks_by_id?.[event_1.webhook_id], cli_clients_by_id?.[event_1.cli_id]);
        const all_fields = [...base_fields, ...dynamic_fields];
        const ordered_fields = column_order.map(index_2 => all_fields[index_2]);
        return [...rows, {
          id: event_1.id,
          selected: selected_event_id === event_1.id && !selected_attempt_id,
          highlighted: selected_event_id === event_1.id,
          outdated,
          expendable: true,
          fields: ordered_fields
        }, ...(selected_event_id !== event_1.id ? [] : [{
          headers: ["Attempt Date", "Status", "Attempt Trigger", "Delivery Latency", "Response Latency"],
          rows: !event_attempts ? [{
            id: event_1.id,
            selected: false,
            highlighted: false,
            selectable: false,
            expendable: false,
            fields: [<Skeleton h={{
              px: 20
            }} w={{
              px: 140
            }} key="1" />, <Skeleton h={{
              px: 20
            }} w={{
              px: 50
            }} key="2" />, <Skeleton h={{
              px: 20
            }} w={{
              px: 200
            }} key="3" />, <Skeleton h={{
              px: 20
            }} w={{
              px: 40
            }} key="4" />, <Skeleton h={{
              px: 20
            }} w={{
              px: 40
            }} key="5" />]
          }] : event_attempts.count === 0 ? [{
            id: "no-attempt",
            highlighted: true,
            selectable: false,
            widths: [{
              min: 100
            }],
            element: <Div p={{
              x: 6,
              y: 2
            }}><Text muted={true} size="s" style={{
                position: "relative",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0
              }}>{event_status_explainer[event_1.status]}</Text></Div>
          }] : event_attempts.models?.map(attempt => ({
            id: attempt.id,
            selected: selected_attempt_id === attempt.id,
            highlighted: selected_event_id === event_1.id,
            fields: getAttemptRowFields(attempt, outdated, event_actions)
          }))
        }])];
      }, []);
    }
    $[6] = active_cli_sessions;
    $[7] = cli_clients;
    $[8] = count;
    $[9] = dynamic_columns;
    $[10] = event_actions;
    $[11] = event_attempts;
    $[12] = events;
    $[13] = fetched;
    $[14] = filters.cli_user_id;
    $[15] = filters.request;
    $[16] = layout;
    $[17] = onAttemptSelected;
    $[18] = onEventSelected;
    $[19] = outdated_ids;
    $[20] = selected_attempt_id;
    $[21] = selected_event_id;
    $[22] = unique_client_ids;
    $[23] = unique_webhook_ids;
    $[24] = webhooks;
    $[25] = alert_top;
    $[26] = draggable_column_indices;
    $[27] = has_request_filter;
    $[28] = headers;
    $[29] = loading;
    $[30] = onRowSelected;
    $[31] = t3;
    $[32] = t4;
    $[33] = widths;
  } else {
    alert_top = $[25];
    draggable_column_indices = $[26];
    has_request_filter = $[27];
    headers = $[28];
    loading = $[29];
    onRowSelected = $[30];
    t3 = $[31];
    t4 = $[32];
    widths = $[33];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  const rows_0 = t3;
  let t5;
  if ($[53] !== reorderDynamicColumns) {
    t5 = (active_index, over_index) => {
      const dynamic_active_index = active_index - 2;
      const dynamic_over_index = over_index - 2;
      reorderDynamicColumns(dynamic_active_index, dynamic_over_index);
    };
    $[53] = reorderDynamicColumns;
    $[54] = t5;
  } else {
    t5 = $[54];
  }
  const handleColumnReorder = t5;
  let t6;
  if ($[55] !== dynamic_columns || $[56] !== updateDynamicColumnWidth) {
    t6 = (visual_index, new_width) => {
      const dynamic_index = visual_index - 2;
      if (dynamic_index >= 0 && dynamic_index < dynamic_columns.length) {
        const field_path = dynamic_columns[dynamic_index].split(":")[0];
        updateDynamicColumnWidth(field_path, new_width);
      }
    };
    $[55] = dynamic_columns;
    $[56] = updateDynamicColumnWidth;
    $[57] = t6;
  } else {
    t6 = $[57];
  }
  const handleColumnResize = t6;
  let t7;
  if ($[58] !== filters.date?.max || $[59] !== is_progressive_loading || $[60] !== scanned_until || $[61] !== timezone) {
    t7 = is_progressive_loading && scanned_until ? `Loaded results up to ${formatDate(scanned_until, "yyyy-MM-dd HH:mm:ss", timezone)} ${getTimezoneAbbreviation(timezone, scanned_until)}` : `Loading events up to ${formatDate(filters.date?.max || new Date(), "yyyy-MM-dd HH:mm", timezone)} ${getTimezoneAbbreviation(timezone, filters.date?.max || new Date())}`;
    $[58] = filters.date?.max;
    $[59] = is_progressive_loading;
    $[60] = scanned_until;
    $[61] = timezone;
    $[62] = t7;
  } else {
    t7 = $[62];
  }
  const load_message = t7;
  const t8 = rows_0 as any;
  const t9 = pagination?.current_page || 1;
  const t10 = pagination?.page_size || 100;
  let t11;
  if ($[63] !== t10 || $[64] !== t9) {
    t11 = {
      current_page: t9,
      page_size: t10,
      entity_name: "events"
    };
    $[63] = t10;
    $[64] = t9;
    $[65] = t11;
  } else {
    t11 = $[65];
  }
  let t12;
  if ($[66] !== next || $[67] !== onPaginationChanged) {
    t12 = next ? () => {
      onPaginationChanged({
        next,
        prev: undefined
      });
    } : undefined;
    $[66] = next;
    $[67] = onPaginationChanged;
    $[68] = t12;
  } else {
    t12 = $[68];
  }
  let t13;
  if ($[69] !== onPaginationChanged || $[70] !== prev) {
    t13 = prev ? () => {
      onPaginationChanged({
        prev,
        next: undefined
      });
    } : undefined;
    $[69] = onPaginationChanged;
    $[70] = prev;
    $[71] = t13;
  } else {
    t13 = $[71];
  }
  let t14;
  if ($[72] !== count || $[73] !== is_progressive_loading || $[74] !== load_message || $[75] !== rows_0 || $[76] !== scanned_until) {
    t14 = is_progressive_loading && scanned_until && rows_0.length > 0 ? <TableBar sticky={true} is_loading={true} load_progress={count}><Text semi={true} muted={true} center={true} p={{
        x: 6,
        y: 1
      }}>{load_message}</Text></TableBar> : undefined;
    $[72] = count;
    $[73] = is_progressive_loading;
    $[74] = load_message;
    $[75] = rows_0;
    $[76] = scanned_until;
    $[77] = t14;
  } else {
    t14 = $[77];
  }
  let t15;
  if ($[78] !== filters.date?.min || $[79] !== loading || $[80] !== max_retention_days || $[81] !== next || $[82] !== next_relative_date || $[83] !== subscription || $[84] !== team || $[85] !== updateFilters) {
    t15 = !loading && !next && next_relative_date ? <TableBar top={true}><Text muted={true} semi={true} center={true} size="s" p={{
        x: 6,
        y: 1
      }}>You've reached the end of the selected date range.{" "}<Link small={true} onClick={() => updateFilters(next_relative_date.key)}>Expand to {next_relative_date.short_label}</Link></Text></TableBar> : !loading && !next && differenceInDays(new Date(filters.date?.min || team.created_at), subDays(new Date(), subscription.retention_days)) <= 0 ? <TableBar top={true}><Text muted={true} semi={true} center={true} size="s" p={{
        x: 6,
        y: 1
      }}>You've reached the end of your {subscription?.retention_days} days archival window.{max_retention_days && subscription.retention_days < max_retention_days && <Link to="/settings/organization/plans?highlight=retention_days" icon="upgrade" m={{
          l: 2
        }}>Upgrade</Link>}</Text></TableBar> : undefined;
    $[78] = filters.date?.min;
    $[79] = loading;
    $[80] = max_retention_days;
    $[81] = next;
    $[82] = next_relative_date;
    $[83] = subscription;
    $[84] = team;
    $[85] = updateFilters;
    $[86] = t15;
  } else {
    t15 = $[86];
  }
  const t16 = has_request_filter ? undefined : total_count;
  let t17;
  if ($[87] !== alert_top || $[88] !== count || $[89] !== draggable_column_indices || $[90] !== handleColumnReorder || $[91] !== handleColumnResize || $[92] !== has_new_results || $[93] !== headers || $[94] !== load_message || $[95] !== loading || $[96] !== onRefresh || $[97] !== onRowSelected || $[98] !== t11 || $[99] !== t12 || $[100] !== t13 || $[101] !== t14 || $[102] !== t15 || $[103] !== t16 || $[104] !== t8 || $[105] !== widths) {
    t17 = <Table fill={true} footer={false} top_offset={0} headers={headers} widths={widths} rows={t8} loading={loading} loading_title={load_message} loading_message={"We\u2019re scanning the selected time range for matching results. If you\u2019d like to find results faster, try a narrower date range."} onRowSelected={onRowSelected} draggable_columns={true} draggable_column_indices={draggable_column_indices} onColumnReorder={handleColumnReorder} onColumnResize={handleColumnResize} pagination={t11} onNextPage={t12} onPreviousPage={t13} sticky_bottom_element={t14} bottom_element={t15} alert_top={alert_top} current_count={count} total_count={t16} has_new_results={has_new_results} onRefresh={onRefresh} />;
    $[87] = alert_top;
    $[88] = count;
    $[89] = draggable_column_indices;
    $[90] = handleColumnReorder;
    $[91] = handleColumnResize;
    $[92] = has_new_results;
    $[93] = headers;
    $[94] = load_message;
    $[95] = loading;
    $[96] = onRefresh;
    $[97] = onRowSelected;
    $[98] = t11;
    $[99] = t12;
    $[100] = t13;
    $[101] = t14;
    $[102] = t15;
    $[103] = t16;
    $[104] = t8;
    $[105] = widths;
    $[106] = t17;
  } else {
    t17 = $[106];
  }
  return t17;
};
export default EventsList;
function _temp(event) {
  return event.webhook_id;
}
function _temp2(event_0) {
  return event_0.cli_id;
}
function _temp3(object, webhook) {
  return {
    ...object,
    [webhook.id]: webhook
  };
}
function _temp4(object_0, webhook_0) {
  return {
    ...object_0,
    [webhook_0.id]: webhook_0
  };
}
function _temp5(v) {
  return !!v;
}
function _temp6(column_string_0) {
  const parsed_0 = parseDynamicColumn(column_string_0);
  return <DynamicColumnHeader key={column_string_0} full_path={parsed_0.display_name} width={parsed_0.width} />;
}
function _temp7(_, index) {
  return index;
}
function _temp8(w) {
  return w !== undefined;
}
function _temp9(h) {
  return h !== undefined;
}
function _temp10(__0, i_1) {
  return i_1 + 2;
}